import { MutableRefObject, useCallback } from 'react'

import type { F0FormRef } from '@factorialco/f0-react'

import { VendorContactDetailsDocument } from 'generated/resources/graphql'

import { useLazyQuery } from 'modules/core/hooks/useQuery'
import { ContactSelectorV2 } from 'modules/finance/components/Contacts/ContactSelectorV2'

interface VendorFieldProps {
  value: string
  onChange: (value: string) => void
  label: string
  required: boolean
  companyId: string
  /** F0FormRef to set computed field values */
  formRef: MutableRefObject<F0FormRef | null>
  /** Form field ID (definition.id) for the vendor_id_number computed field */
  vendorIdNumberFieldId: string | null
  /** Form field ID (definition.id) for the address computed field */
  addressFieldId: string | null
}

function formatVendorAddress(address: {
  line1: string
  line2?: string | null
  postalCode: string
  city: string
  state: string
  countryCode: string
}): string {
  return [
    address.line1,
    address.line2,
    address.postalCode,
    address.city,
    address.state,
    address.countryCode,
  ]
    .filter(Boolean)
    .join(', ')
}

function VendorField({
  value,
  onChange,
  label,
  required,
  companyId,
  formRef,
  vendorIdNumberFieldId,
  addressFieldId,
}: VendorFieldProps) {
  const [fetchContact] = useLazyQuery(VendorContactDetailsDocument, {
    onCompleted(data) {
      const contact = data.finance.contactsConnection.edges[0]?.node
      if (!contact) return

      if (vendorIdNumberFieldId) {
        formRef.current?.setValue(vendorIdNumberFieldId, contact.taxId ?? '')
      }
      if (addressFieldId) {
        const formatted = contact.address ? formatVendorAddress(contact.address) : ''
        formRef.current?.setValue(addressFieldId, formatted)
      }
    },
  })

  const clearComputedFields = useCallback(() => {
    if (vendorIdNumberFieldId) {
      formRef.current?.setValue(vendorIdNumberFieldId, '')
    }
    if (addressFieldId) {
      formRef.current?.setValue(addressFieldId, '')
    }
  }, [vendorIdNumberFieldId, addressFieldId, formRef])

  const handleChange = useCallback(
    (id: string | undefined) => {
      const newValue = id ?? ''
      onChange(newValue)

      if (newValue) {
        fetchContact({ variables: { ids: [newValue] } })
      } else {
        clearComputedFields()
      }
    },
    [onChange, fetchContact, clearComputedFields]
  )

  return (
    <ContactSelectorV2
      label={label}
      value={value || undefined}
      onChange={handleChange}
      forcedFilters={{ companyId, contactType: 'vendor' as const }}
      mandatory={required}
      hideSearchIcon
    />
  )
}

export default VendorField
